import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  DEFAULT_SETTINGS,
  ZONA_HORARIA,
  cordobasToUsd,
  esCuentaEnDolares,
  formatMoney,
  fxRate,
  hoy,
  idDeOcurrencia,
  newId,
  parseMoney,
  usdToCordobas,
  type Centavos,
  type CuentaBanco,
  type CuentaBancoCordobas,
  type GastoFijo,
  type Settings,
} from '@abastece/shared';
import {
  CATEGORIAS_GASTO,
  efectivoEsperado,
  esDeudaAnterior,
  pendienteDeCompra,
  pendienteDeGasto,
  type CategoriaGasto,
  type Compra,
  type FormaPago,
  type Gasto,
  type MovimientoDueno,
  type PagoProveedor,
} from '@abastece/core';
import { db } from '../db/dexie.js';
import { leerSinEfecto, tuvoEfecto } from '../lib/sinEfecto.js';
import { readSettings } from '../db/reader.js';
import { cuentaEnCordobas, cuentaQueQueda, delDuenoEnEfectivo, delDuenoPorBanco } from '../lib/bancos.js';
import { pagoEnPartes, restoDeLasPartes } from '../lib/pagos-de-compra.js';
import { emitir, emitirYVer, probarEvento } from '../lib/emitir.js';
import {
  anulacionQueQuedo,
  busquedaDeGastos,
  cajaDeCorreccion,
  correccionMueveDinero,
  deLaGavetaDelGasto,
  filtrarGastos,
  filtroInicial,
  filtrosPuestos,
  ordenDeCorreccion,
  pagoDeLaDeuda,
  partesDelGasto,
  sePuedeCorregir,
  sumaGastos,
  FORMAS_GASTO,
  type FiltroGastos,
  type FormaGasto,
  type VistaGastos,
} from '../lib/gastos.js';
import { dolaresDeLosLibros, useMonedaDeLosLibros } from '../lib/libros.js';
import { usePuede } from '../lib/sesion.js';
import { Campo, Interruptor, Selector } from '../ui/Campo.js';
import { FilaMoneda } from '../ui/ElegirMoneda.js';
import { ElegirEscribiendo } from '../ui/ElegirEscribiendo.js';
import { ElegirCuenta } from '../ui/ElegirCuenta.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { Ico } from '../ui/Iconos.js';
import { Confirmar } from '../ui/Preguntar.js';
import { CADA, EditarGastoFijo, ListaGastosFijos, TocaPagar, nombreMes } from '../ui/GastosFijos.js';
import { CADA_MESES, estadoDeId, leerAnulaciones, pendientes, type Ocurrencia } from '../lib/gastosFijos.js';
import { PedirPin, usePinParaAnular } from '../ui/PedirPin.js';
import { SaleDeLaGaveta, faltaEnGaveta, useGaveta } from '../ui/SaleDeLaGaveta.js';
import { PartesDelPago, type ParteEscrita } from '../ui/PartesDelPago.js';
import { enMoneda } from '../ui/ContarBilletes.js';
import { TomarFotoCedula, VerFotoCedula } from '../ui/FotoCedula.js';
import { DeudaAnterior } from '../ui/DeudaAnterior.js';
import { AnularMovimiento, AnularPago } from '../ui/Correcciones.js';
import { AbonarDeuda, type DeudaPorAbonar } from '../ui/AbonarDeuda.js';
import { FilaMovimientoDueno, FilaPagoProveedor } from '../ui/FilasDeDinero.js';
import { leerPagosHechos } from '../lib/leerPagos.js';
import { fechaCorta } from '../ui/CuentasDeCaja.js';
import { Modal } from '../ui/Modal.js';
import { BuscarEnLista, Desplegable, FilaDesplegables, FiltroFechas, FiltrosEnLinea, VerTodos } from '../ui/FiltrosHistorial.js';
import { localeActual, useT, type Clave } from '../i18n/index.js';
import { etiquetaCategoriaGasto } from '../i18n/dominio.js';
import { enRango } from '../lib/costos.js';

type Forma = FormaGasto;
const FORMAS = FORMAS_GASTO;
const nombreForma = (f: string): Clave => FORMAS.find((x) => x.valor === f)?.nombre ?? 'pago.efectivo_cordobas';

/**
 * A tab for each job (`VISTAS_GASTOS`). Two carry an anchor for the «Gastos y pagos» lesson: what
 * the owner takes out is not an expense and goes under «Dueño», and what is owed is under «Por pagar».
 */
const VISTAS: { clave: VistaGastos; nombre: Clave; guia?: string }[] = [
  { clave: 'gastos', nombre: 'gastos.vistaGastos' },
  { clave: 'porPagar', nombre: 'gastos.vistaPorPagar', guia: 'gastos-ver-por-pagar' },
  { clave: 'pagos', nombre: 'gastos.vistaPagos' },
  { clave: 'dueno', nombre: 'gastos.vistaDueno', guia: 'gastos-ver-dueno' },
];

/** An expense as its form holds it: the amount as typed, the supplier as the select has it. */
interface Borrador {
  fecha: string;
  categoria: CategoriaGasto;
  descripcion: string;
  monto: string;
  moneda: 'NIO' | 'USD';
  /** What pays what the parts leave, or all of it without parts. */
  formaPago: Forma;
  /**
   * Parts paid some other way (the owner, 2026-10-16: «We need the option to pay with mixed methods
   * of payment»), each typed in the expense's currency: «¿Cómo se pagó?» pays what they leave.
   */
  partes: ParteEscrita[];
  proveedorId: string;
  referencia: string;
  foto: string | null;
  /** By transfer, the bank account it came from; null for where transfers go (`cuentaTransferencias`). */
  cuenta: CuentaBanco | null;
}

/** The account a draft paid by transfer comes from: the one picked while it fits, or where transfers go. */
const cuentaDelBorrador = (config: Settings, b: Borrador): CuentaBanco | null =>
  b.formaPago === 'transferencia' ? cuentaQueQueda(config, b.cuenta, b.moneda) : null;

/** A draft's parts read, in its currency: null while one does not read as an amount. */
const montosDePartes = (b: Borrador) => b.partes.map((x) => parseMoney(x.monto));
/** What the parts leave for «¿Cómo se pagó?», in the draft's currency: below nothing, they pay too much. */
const restoDelBorrador = (b: Borrador) => restoDeLasPartes(parseMoney(b.monto) ?? 0, montosDePartes(b).map((m) => ({ monto: m ?? 0 })));
/** Whether «¿Cómo se pagó?» pays anything: always without parts, and while they leave something. */
const pagaElResto = (b: Borrador) => b.partes.length === 0 || restoDelBorrador(b) > 0;
/** Córdobas in cash anywhere in it, part or rest: what the drawer's one switch is for, as on a purchase. */
const conEfectivo = (b: Borrador) =>
  (pagaElResto(b) && b.formaPago === 'efectivo_cordobas') || b.partes.some((x) => x.formaPago === 'efectivo_cordobas');
/** Of it, the córdobas in cash, in the draft's currency: what the drawer pays when its switch is on. */
const enEfectivo = (b: Borrador) =>
  b.partes.reduce((a, x, i) => a + (x.formaPago === 'efectivo_cordobas' ? (montosDePartes(b)[i] ?? 0) : 0), 0) +
  (pagaElResto(b) && b.formaPago === 'efectivo_cordobas' ? restoDelBorrador(b) : 0);
/** An amount in the draft's currency in córdobas, as the books take it: null past what they take. */
const aCordobas = (b: Borrador, monto: number, tasa: number): Centavos | null =>
  b.moneda === 'USD' ? enRango(() => usdToCordobas(monto as Centavos, fxRate(tasa))) : (monto as Centavos);

/**
 * How the draft was paid, as `gasto.registrado` carries it (`pagoEnPartes`): its parts as `pagos`,
 * each part by transfer with its córdoba account, and the drawer's till for every córdoba in cash.
 */
function pagoDelBorrador(config: Settings, b: Borrador, sesionId: string | undefined, nuevosIds = false) {
  return pagoEnPartes({
    total: parseMoney(b.monto) ?? 0,
    partes: b.partes.map((x, i) => ({
      // A correction's parts are new payments: the old ones go back with the expense they were of.
      pagoId: nuevosIds ? newId() : x.pagoId,
      formaPago: x.formaPago,
      monto: montosDePartes(b)[i]!,
      ...(x.formaPago === 'transferencia' ? { cuenta: cuentaEnCordobas(config, x.cuenta) } : {}),
    })),
    formaPago: b.formaPago,
    sesionId,
    cuenta: cuentaDelBorrador(config, b) ?? undefined,
  });
}

const borradorNuevo = (): Borrador => ({
  fecha: hoy(),
  categoria: 'otros',
  descripcion: '',
  monto: '',
  moneda: 'NIO',
  formaPago: 'efectivo_cordobas',
  partes: [],
  proveedorId: '',
  referencia: '',
  foto: null,
  cuenta: null,
});

/** In the expense's currency: a part in dollars keeps its dollars (`dolares`), as typed. */
const comoSeEscribio = (g: Gasto, x: PagoProveedor) =>
  ((g.moneda === 'USD' ? (x.dolares ?? cordobasToUsd(x.monto, fxRate(g.tasaCambio ?? 1))) : x.monto) / 100).toFixed(2);

const borradorDe = (g: Gasto): Borrador => ({
  fecha: g.fecha,
  categoria: g.categoria,
  descripcion: g.descripcion,
  monto: (g.monto / 100).toFixed(2),
  moneda: g.moneda,
  formaPago: g.formaPago as Forma,
  partes: partesDelGasto(g).map((x) => ({
    pagoId: x.id,
    formaPago: x.formaPago,
    monto: comoSeEscribio(g, x),
    cuenta: x.formaPago === 'transferencia' ? ((x.cuenta as CuentaBancoCordobas | null | undefined) ?? 'BANCOS') : null,
  })),
  proveedorId: g.proveedorId ?? '',
  referencia: g.referencia ?? '',
  foto: g.foto ?? null,
  // One from before there were other accounts went to «Bancos», and stays there unless changed.
  cuenta: g.formaPago === 'transferencia' ? ((g.cuenta as CuentaBanco | null | undefined) ?? 'BANCOS') : null,
});

/** A form as the books would record it: "150" and "150.00 " are one amount, and no change. */
const comoQueda = (b: Borrador) =>
  JSON.stringify({
    ...b,
    monto: parseMoney(b.monto),
    descripcion: b.descripcion.trim(),
    referencia: b.referencia.trim(),
    partes: b.partes.map((x) => ({ formaPago: x.formaPago, monto: parseMoney(x.monto), cuenta: x.cuenta })),
  });

const cuandoConHora = (iso: string) =>
  new Date(iso).toLocaleString(localeActual(), {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: ZONA_HORARIA,
  });

export function Gastos() {
  const t = useT();
  const puedeVer = usePuede('ver_reportes');
  const puedeRegistrar = usePuede('configurar');
  /**
   * What the shop already owed its suppliers the day it started here goes in beside what it owes
   * now: against Capital, so the owner's (`compra.saldo_inicial`), not whoever receives purchases.
   */
  const puedeAnotarDeuda = usePuede('configurar');

  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  const gastos = useLiveQuery(() => db.gastos.toArray(), [], []);
  const compras = useLiveQuery(() => db.compras.toArray(), [], []);
  const proveedores = useLiveQuery(() => db.proveedores.toArray(), [], []);
  /** Undefined while still being read: «Dueño» says it has nothing only once it knows. */
  const movimientos = useLiveQuery(() => db.movimientosDueno.toArray(), []);
  /**
   * The latest payments to suppliers, of purchases and of expenses bought on credit
   * (`pagosHechos`): all of them are in «Pagos a proveedores», a tap away. Undefined while still
   * being read, as the owner's money is.
   */
  const pagos = useLiveQuery(async () => (await leerPagosHechos()).slice(0, 20), []);
  /** A payment or the owner's money being voided. */
  const [anulandoPago, setAnulandoPago] = useState<{ deuda: { tipo: 'compra' | 'gasto'; id: string }; pago: PagoProveedor; nombre: string } | null>(null);
  const [anulandoMovimiento, setAnulandoMovimiento] = useState<MovimientoDueno | null>(null);
  /** Each expense's voids, oldest first: one that named what took its place was a correction. */
  const anulaciones = useLiveQuery(() => leerAnulaciones(), [], new Map<string, { motivo: string; reemplazo?: string }[]>());
  /**
   * Voided and replaced by the correction it names, which stands in the books: one recorded after its
   * void (`reemplazoDespues`) may have been refused, and then nothing replaced it.
   */
  const corregido = (g: Gasto) => {
    const reemplazo = anulacionQueQuedo(g, anulaciones.get(g.id) ?? [])?.reemplazo;
    return reemplazo !== undefined && gastos.some((x) => x.id === reemplazo);
  };

  // Sanitized here as well as by the route, as «Análisis» does: the root route passes every
  // parameter of the address through, and a «?vista=» typed by hand must open on the expenses.
  const busqueda = busquedaDeGastos(useSearch({ from: '/gastos' }));
  const { nuevo } = busqueda;
  const vista = busqueda.vista ?? 'gastos';
  /**
   * Córdobas or dollars (2026-10-13), the phone's one choice for the owner's figures, every tab's
   * lists and totals: what is typed, an expense, a payment, a correction, stays in córdobas, and an
   * expense opens as it was recorded. `enFila`, the rows' dollars, only when the page is in them.
   */
  const libros = useMonedaDeLosLibros();
  const enDolares = libros.moneda === 'USD';
  const enFila = enDolares ? (c: Centavos) => libros.dinero(c) : undefined;
  /** An expense in dollars says its own dollars, what it cost; the rest are turned at the official rate. */
  const dolaresDe = (g: Gasto): number => (g.moneda === 'USD' ? g.monto : cordobasToUsd(g.montoCordobas, libros.tasa));
  const montoDeGasto = (g: Gasto): string => (enDolares ? dolaresDeLosLibros(dolaresDe(g)) : formatMoney(g.montoCordobas));
  const navigate = useNavigate();
  /**
   * Another tab is the same screen: nothing slides, as `nuevo` leaving the address. In place of
   * the entry, as the section's tabs are, so the phone's back button leaves the screen instead of
   * stepping back through the tabs.
   */
  const ir = (v: VistaGastos) =>
    void navigate({ to: '/gastos', search: busquedaDeGastos({ vista: v }), replace: true, viewTransition: false });
  /**
   * «Nuevo gasto»'s sheet, over whichever tab is open (the owner, 2026-10-06: «the new expense button
   * should open a modal form instead of spawning a form inline on top of the tabs»), and the owner's
   * money's its own: «in fact any … form that does that … should instead be replaced by a form that
   * appears in a modal».
   */
  const [nuevoAbierto, setNuevoAbierto] = useState(() => Boolean(nuevo && puedeRegistrar));
  const [duenoAbierto, setDuenoAbierto] = useState(false);
  /**
   * A month of a fixed expense being recorded from «Toca pagar» (the owner, 2026-10-07), and the
   * half-typed expense it set aside: the sheet is the same, filled in from the fixed expense.
   */
  const [deFijo, setDeFijo] = useState<Ocurrencia | null>(null);
  const [apartado, setApartado] = useState<Borrador | null>(null);
  /** Its «Sale de la gaveta», set aside with it: the month starts with the switch off. */
  const [apartadoDeLaGaveta, setApartadoDeLaGaveta] = useState(false);
  /** «Se repite»: the expense being recorded becomes a fixed one, and this is its first month. */
  const [repite, setRepite] = useState(false);
  const [repiteCada, setRepiteCada] = useState<number>(1);
  const [repiteVaria, setRepiteVaria] = useState(false);
  /** A fixed expense open in its sheet ('nuevo' for a new one), and a month being passed over. */
  const [editandoFijo, setEditandoFijo] = useState<GastoFijo | 'nuevo' | null>(null);
  const [omitiendo, setOmitiendo] = useState<Ocurrencia | null>(null);
  /*
   * «Nuevo gasto» on the home screen asks for the form once, and the address forgets it at once:
   * back here later, by a reload or by «Volver» from another section, the list is as it was left.
   * With no transition of its own: it lands while the slide in from the home screen is still
   * playing, before the router counts that arrival as done, so it would look like a second
   * change of screen and cut the first one short.
   */
  useEffect(() => {
    if (nuevo) void navigate({ to: '/gastos', search: {}, replace: true, viewTransition: false });
  }, [nuevo, navigate]);
  const [borrador, setBorrador] = useState<Borrador>(borradorNuevo);
  const cambiarBorrador = (parte: Partial<Borrador>) => setBorrador((b) => ({ ...b, ...parte }));
  // The open till's drawer pays only what is paid today, as the wizard has it (`PasosDeGasto`): an
  // expense of another day out of it came off today's till, which came up over, and that day's short.
  // Córdobas in cash, part or rest; out of a till open on another device too (the owner, 2026-10-16:
  // «We need an option to be able to pay an expense from the till»), from a phone with none of its own.
  // Off until somebody turns it on, this device's till too (2026-10-17: «the checkbox that says "sale
  // de la gaveta" shouldn't be checked by default»), as on a purchase.
  const gaveta = useGaveta(borrador.fecha === hoy() && conEfectivo(borrador) ? 'efectivo_cordobas' : '', { encendidaAlEmpezar: false, deOtroEquipo: true });
  const [errorGasto, setErrorGasto] = useState<string | null>(null);

  const [tipoDueno, setTipoDueno] = useState<'retiro' | 'aporte'>('retiro');
  const [montoDueno, setMontoDueno] = useState('');
  const [motivoDueno, setMotivoDueno] = useState('');
  const [cuentaDueno, setCuentaDueno] = useState<Exclude<Forma, 'credito'>>('efectivo_cordobas');
  /** By transfer, the bank account: any of the shop's, a dollar account's in its dollars. */
  const [bancoElegidoDueno, setBancoElegidoDueno] = useState<CuentaBanco | null>(null);
  const bancoDueno = cuentaQueQueda(config, bancoElegidoDueno, 'USD');
  const duenoEnDolares = (cuentaDueno === 'transferencia' && esCuentaEnDolares(bancoDueno)) || cuentaDueno === 'efectivo_dolares';

  const [trabajando, setTrabajando] = useState(false);
  /** What is being paid off: a purchase on credit, or an expense bought on credit (`AbonarDeuda`). */
  const [abonando, setAbonando] = useState<DeudaPorAbonar | null>(null);
  const [deuda, setDeuda] = useState<Compra | 'nueva' | null>(null);

  const [filtro, setFiltro] = useState<FiltroGastos>(filtroInicial);
  /** A hundred at a time: «Todo» over a few years is thousands of rows. */
  const [cuantosVer, setCuantosVer] = useState(100);
  const cambiarFiltro = (parte: Partial<FiltroGastos>) => {
    setFiltro((f) => ({ ...f, ...parte }));
    setCuantosVer(100);
  };
  /** The expense whose sheet is open, and the one being corrected from there. */
  const [viendo, setViendo] = useState<string | null>(null);
  const [corrigiendo, setCorrigiendo] = useState<Gasto | null>(null);

  const porProveedor = useMemo(() => new Map(proveedores.map((p) => [p.id, p.nombre])), [proveedores]);
  const hoyEs = hoy();
  /** The months of the fixed expenses still to record: recorded is an expense that stands, or its correction. */
  const pendientesFijos = useMemo(() => {
    const porId = new Map(gastos.map((g) => [g.id, g]));
    return pendientes(config.gastosFijos, hoyEs, (id) => estadoDeId(id, (x) => porId.get(x), anulaciones));
  }, [config.gastosFijos, gastos, anulaciones, hoyEs]);
  const visibles = useMemo(
    () => filtrarGastos(gastos, filtro, hoy(), (id) => porProveedor.get(id)),
    [gastos, filtro, porProveedor],
  );

  async function guardarGasto() {
    const valor = parseMoney(borrador.monto);
    if (valor === null || valor <= 0 || !borrador.descripcion.trim()) return;
    if (borrador.fecha > hoy()) {
      setErrorGasto(t('gastoPaso.fechaFutura'));
      return;
    }
    // Each part read, and together under the whole: what they leave is always something to pay.
    if (montosDePartes(borrador).some((x) => x === null || x <= 0)) {
      setErrorGasto(t('compra.faltaMontoParte'));
      return;
    }
    const resto = restoDelBorrador(borrador);
    if (resto < 0) {
      setErrorGasto(t('gastos.partesDeMas', { monto: enMoneda(-resto as Centavos, borrador.moneda) }));
      return;
    }
    const enCordobas = aCordobas(borrador, valor, config.fxTasaOficial);
    // What the drawer pays, when its switch is on: every córdoba in cash, part or rest.
    const deLaGaveta = aCordobas(borrador, enEfectivo(borrador), config.fxTasaOficial);
    if (enCordobas === null || deLaGaveta === null) {
      setErrorGasto(t('gastos.demasiado'));
      return;
    }
    const problema = faltaEnGaveta(gaveta, deLaGaveta, t);
    setErrorGasto(problema);
    if (problema) return;
    setTrabajando(true);
    try {
      // A month of a fixed expense takes that month's id, the same on every phone, so the books keep
      // it once; «Se repite» makes the fixed expense, and this expense is its first month.
      const fijoNuevo = !deFijo && repite ? { id: newId(), periodo: borrador.fecha.slice(0, 7) } : null;
      const enlace = deFijo ? { id: deFijo.fijo.id, periodo: deFijo.periodo } : fijoNuevo;
      const gastoId = deFijo ? deFijo.gastoId : fijoNuevo ? idDeOcurrencia(fijoNuevo.id, fijoNuevo.periodo) : newId();
      const { rechazo } = await emitirYVer('gasto.registrado', {
        gastoId,
        fecha: borrador.fecha,
        categoria: borrador.categoria,
        descripcion: borrador.descripcion.trim(),
        moneda: borrador.moneda,
        monto: valor,
        // The official rate: what dollars are carried at on the books, general cash included.
        ...(borrador.moneda === 'USD' ? { tasaCambio: config.fxTasaOficial } : {}),
        ...pagoDelBorrador(config, borrador, gaveta.sesionId),
        proveedorId: borrador.proveedorId || null,
        ...(borrador.referencia.trim() ? { referencia: borrador.referencia.trim() } : {}),
        ...(borrador.foto ? { foto: borrador.foto } : {}),
        ...(enlace ? { gastoFijo: enlace } : {}),
      });
      // Refused, it is said and the form stays as it was: an ordinary expense used to close as if
      // recorded (2026-10-13). Another phone recorded the same month first: said so, not taken twice.
      if (rechazo) {
        setErrorGasto(deFijo && (await db.gastos.get(gastoId)) ? t('fijos.yaRegistrado') : rechazo);
        return;
      }
      if (fijoNuevo) {
        await guardarFijos([
          ...config.gastosFijos,
          {
            id: fijoNuevo.id,
            descripcion: borrador.descripcion.trim().slice(0, 160),
            categoria: borrador.categoria,
            moneda: borrador.moneda,
            monto: repiteVaria ? null : valor,
            formaPago: borrador.formaPago,
            ...(borrador.formaPago === 'transferencia' ? { cuenta: cuentaDelBorrador(config, borrador)! } : {}),
            proveedorId: borrador.proveedorId || null,
            dia: Number(borrador.fecha.slice(8, 10)),
            cadaMeses: repiteCada,
            desde: fijoNuevo.periodo,
            omitidos: [],
            activo: true,
          },
        ]);
      }
      // A month of a fixed expense leaves the half-typed expense it set aside as it was, «Se repite» and
      // the drawer too; after any other, the next starts with the drawer's switch off, as this one did.
      if (!deFijo) {
        cambiarBorrador({ descripcion: '', monto: '', referencia: '', foto: null, partes: [] });
        setRepite(false);
        setRepiteCada(1);
        setRepiteVaria(false);
        gaveta.setEncendido(false);
      }
      cerrarNuevo();
    } catch (e) {
      setErrorGasto(e instanceof Error ? e.message : t('entrega.noSeGuardo'));
    } finally {
      setTrabajando(false);
    }
  }

  /** The fixed expenses as they now are: a setting like any other, the owner's. */
  async function guardarFijos(lista: GastoFijo[]) {
    await emitir('config.cambiada', { clave: 'gastosFijos', valor: lista, anterior: config.gastosFijos });
  }

  /** «Registrar» on a month in «Toca pagar»: the new expense's sheet, filled in from the fixed expense. */
  function registrarFijo(o: Ocurrencia) {
    setApartado(borrador);
    setApartadoDeLaGaveta(gaveta.encendido);
    gaveta.setEncendido(false);
    setBorrador({
      fecha: hoy(),
      categoria: o.fijo.categoria,
      descripcion: t('fijos.descripcionMes', { descripcion: o.fijo.descripcion, mes: nombreMes(o.periodo) }),
      monto: o.fijo.monto === null ? '' : (o.fijo.monto / 100).toFixed(2),
      moneda: o.fijo.moneda,
      formaPago: o.fijo.formaPago,
      partes: [],
      proveedorId: o.fijo.proveedorId ?? '',
      referencia: '',
      foto: null,
      cuenta: o.fijo.cuenta ?? null,
    });
    setErrorGasto(null);
    setDeFijo(o);
    setNuevoAbierto(true);
  }

  /** Closed, a month of a fixed expense gives back the half-typed expense it set aside. */
  function cerrarNuevo() {
    setNuevoAbierto(false);
    if (deFijo) {
      setDeFijo(null);
      if (apartado) setBorrador(apartado);
      setApartado(null);
      gaveta.setEncendido(apartadoDeLaGaveta);
    }
  }

  async function guardarDueno() {
    const valor = parseMoney(montoDueno);
    if (valor === null || valor <= 0) return;
    setTrabajando(true);
    try {
      await emitir(tipoDueno === 'retiro' ? 'dueno.retiro' : 'dueno.aporte', {
        movimientoId: newId(),
        fecha: hoy(),
        ...(cuentaDueno === 'transferencia' ? delDuenoPorBanco(config, bancoDueno, valor) : delDuenoEnEfectivo(config, cuentaDueno, valor)),
        ...(tipoDueno === 'retiro' ? { origen: cuentaDueno } : { destino: cuentaDueno }),
        ...(motivoDueno.trim() ? { motivo: motivoDueno.trim() } : {}),
      } as never);
      setMontoDueno('');
      setMotivoDueno('');
      setDuenoAbierto(false);
    } finally {
      setTrabajando(false);
    }
  }

  if (!puedeVer) {
    return (
      <Pagina titulo={t('gastos.titulo')}>
        <Vacio mensaje={t('comun.esDelDueno')} />
      </Pagina>
    );
  }

  const vigentes = gastos.filter((g) => !g.anulado).sort((a, b) => b.fecha.localeCompare(a.fecha));
  /**
   * The same amount, category and day already recorded: the usual way one bill gets paid twice —
   * typed again from the other phone, or once from the receipt and once from memory. Said, not
   * refused: two identical bottles of gas on one day happen.
   */
  const montoEscrito = parseMoney(borrador.monto);
  const repetido =
    montoEscrito !== null && montoEscrito > 0
      ? vigentes.find(
          (g) =>
            g.fecha === borrador.fecha &&
            g.categoria === borrador.categoria &&
            g.moneda === borrador.moneda &&
            g.monto === montoEscrito,
        )
      : undefined;
  // What is still owed, less what went back to the supplier and came off the bill.
  const porPagar = compras
    .filter((c) => pendienteDeCompra(c) > 0)
    .sort((a, b) => (a.vencePago ?? '').localeCompare(b.vencePago ?? ''));
  // Expenses bought on credit are owed too, the oldest first: they carry no due date.
  const gastosPorPagar = vigentes.filter((g) => pendienteDeGasto(g) > 0).reverse();
  const totalPorPagar =
    porPagar.reduce((a, c) => a + pendienteDeCompra(c), 0) + gastosPorPagar.reduce((a, g) => a + pendienteDeGasto(g), 0);
  const suma = sumaGastos(visibles);
  /**
   * What «Por pagar» says from the other tabs: a dot while a supplier is owed, red once a bill is past
   * due. A fixed expense whose day went by is red too, and one due today amber, each saying so.
   */
  const aviso: Clave | null = porPagar.some((c) => c.vencePago !== null && c.vencePago < hoy())
    ? 'gastos.hayVencidas'
    : pendientesFijos.some((o) => o.estado === 'vencido')
      ? 'fijos.hayVencidos'
      : porPagar.length + gastosPorPagar.length > 0
        ? 'gastos.hayPorPagar'
        : pendientesFijos.some((o) => o.estado === 'hoy')
          ? 'fijos.hayHoy'
          : null;
  const avisoRojo = aviso === 'gastos.hayVencidas' || aviso === 'fijos.hayVencidos';

  return (
    <Pagina
      titulo={t('gastos.titulo')}
      subtitulo={t('gastos.registrados', { n: vigentes.length })}
      volverA="/contabilidad"
      acciones={
        puedeRegistrar && (
          <button
            type="button"
            className="btn-primario gap-1.5 px-4 text-sm"
            data-guia="gastos-nuevo"
            onClick={() => setNuevoAbierto(true)}
          >
            <Ico icono="nuevo" className="size-4" />
            {t('gastos.nuevo')}
          </button>
        )
      }
    >
      {/*
        A tab for each job, as the owner asked (2026-10-06), where they all used to stand one under
        the other. Underlined, as «Contabilidad»'s are, so they read as parts of this screen and not
        as the section's strip above its name. The four share the row as their words need: whole on
        a 320 px phone, with nothing to slide sideways.
      */}
      <nav className="mb-4 flex border-b border-slate-800" aria-label={t('gastos.vistas')} data-gastos-vistas>
        {VISTAS.map((v) => {
          const activa = v.clave === vista;
          return (
            <button
              key={v.clave}
              type="button"
              onClick={() => ir(v.clave)}
              aria-pressed={activa}
              data-sin-icono="pestana"
              data-gastos-vista={v.clave}
              data-guia={v.guia}
              // One weight for all: a bolder open tab is a wider one, and the row shifted as it moved.
              className={`-mb-px flex min-h-11 min-w-0 flex-auto items-center justify-center gap-1.5 border-b-2 px-2 text-sm font-medium transition max-[359px]:px-1 ${
                activa ? 'border-marca-500 text-slate-100' : 'border-transparent text-slate-400 hover:border-slate-500 hover:text-slate-200'
              }`}
            >
              <span className="truncate">{t(v.nombre)}</span>
              {v.clave === 'porPagar' && aviso && (
                <span
                  role="img"
                  aria-label={t(aviso)}
                  title={t(aviso)}
                  data-gastos-aviso={avisoRojo ? 'vencida' : 'debe'}
                  className={`size-2 shrink-0 rounded-full ${avisoRojo ? 'bg-rose-400' : 'bg-amber-400'}`}
                />
              )}
            </button>
          );
        })}
      </nav>
      <FilaMoneda moneda={libros.moneda} onElegir={libros.elegir} tasa={libros.tasa} />

      {vista === 'gastos' &&
        (gastos.length === 0 ? (
          <Vacio mensaje={t('gastos.ninguno')} />
        ) : (
          <section className="mb-4" data-gastos-lista>
            <h2 className="mb-3 text-sm font-semibold text-slate-400">{t('gastos.listaTitulo')}</h2>
            <FiltrosGastos filtro={filtro} cambiar={cambiarFiltro} proveedores={proveedores} />
            <p className="mb-2 text-sm text-slate-300" data-gastos-suma>
              {t('gastos.suma', {
                // In dollars, what the rows say added up: an expense in dollars at what it cost.
                total: enDolares ? dolaresDeLosLibros(visibles.reduce((a, g) => a + (g.anulado ? 0 : dolaresDe(g)), 0)) : formatMoney(suma.total),
                n: suma.cuantos,
              })}
            </p>
            {visibles.length === 0 ? (
              <p className="tarjeta text-sm text-slate-500">{t('gastos.ningunoAsi')}</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {visibles.slice(0, cuantosVer).map((g) => (
                  <li key={g.id}>
                    <button
                      type="button"
                      data-sin-icono="fila"
                      onClick={() => setViendo(g.id)}
                      className={`tarjeta-accion flex w-full items-center justify-between gap-3 text-left ${g.anulado ? 'opacity-50' : ''}`}
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{g.descripcion}</span>
                        <span className="mt-0.5 block truncate text-xs text-slate-500">
                          {/* First, where the line's end is cut off on a narrow phone. */}
                          {g.anulado && (
                            <span className="text-rose-400">
                              {t(corregido(g) ? 'gastos.corregido' : 'gastos.anulado')} ·{' '}
                            </span>
                          )}
                          {etiquetaCategoriaGasto(g.categoria)} · {g.fecha}
                          {g.faltanteDe ? t('gastos.deFaltante') : ''}
                          {g.moneda === 'USD' && !enDolares ? ` · US$${(g.monto / 100).toFixed(2)}` : ''}
                          {(g.pagos ?? []).some((x) => x.conElGasto) ? t('gastos.enPartes') : ''}
                          {g.formaPago === 'credito' && !g.anulado
                            ? t(pendienteDeGasto(g) > 0 ? 'gastos.quedaDebiendo' : 'gastos.yaPagado')
                            : ''}
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center gap-2">
                        {g.foto && (
                          <span role="img" aria-label={t('gastos.recibo')} title={t('gastos.recibo')}>
                            <Ico icono="foto" className="size-4 text-slate-500" />
                          </span>
                        )}
                        <span className={`tabular ${g.anulado ? 'line-through' : ''}`}>{montoDeGasto(g)}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {visibles.length > cuantosVer && (
              <button
                type="button"
                className="btn-secundario mt-3 w-full text-sm"
                onClick={() => setCuantosVer((n) => n + 100)}
              >
                <Ico icono="desplegar" />
                {t('comun.verMas')}
              </button>
            )}
          </section>
        ))}

      {vista === 'porPagar' && (
        <div className="flex flex-col gap-4">
          {/* What is due of the fixed expenses first: the rent before what is owed to the suppliers. */}
          {pendientesFijos.length > 0 && (
            <TocaPagar pendientes={pendientesFijos} puedeRegistrar={puedeRegistrar} onRegistrar={registrarFijo} onOmitir={setOmitiendo} dinero={enFila} />
          )}
          {/* Shown even with nothing owed: it says so, and holds «Deuda anterior». */}
          <section className="tarjeta" data-guia="gastos-por-pagar">
            <div className="mb-2 flex items-baseline justify-between gap-3">
              <h2 className="text-sm font-semibold text-amber-200">
                {t('gastos.cuentasPorPagar')}
              </h2>
              <span className="tabular shrink-0 whitespace-nowrap text-sm">{libros.dinero(totalPorPagar as Centavos)}</span>
            </div>
            {porPagar.length === 0 && gastosPorPagar.length === 0 && (
              <p className="text-xs text-slate-500">{t('gastos.nadaPorPagar')}</p>
            )}
            <ul className="flex flex-col gap-2">
              {porPagar.map((c) => {
                const saldo = pendienteDeCompra(c);
                const vencida = c.vencePago !== null && c.vencePago < hoy();
                const anterior = esDeudaAnterior(c);
                return (
                  <li key={c.id} className="flex items-center justify-between gap-3 text-sm">
                    <div className="min-w-0">
                      <p className="truncate">
                        {proveedores.find((p) => p.id === c.proveedorId)?.nombre ??
                          t('gastos.proveedor')}
                      </p>
                      <p className={`text-xs ${vencida ? 'text-rose-300' : 'text-slate-500'}`}>
                        {c.documento ? `${c.documento} · ` : ''}
                        {c.vencePago
                          ? t('gastos.vence', { fecha: c.vencePago })
                          : t('gastos.sinFecha')}
                        {c.pagado > 0 ? t('gastos.abonado', { monto: libros.dinero(c.pagado) }) : ''}
                        {anterior ? t('gastos.deAntes') : ''}
                      </p>
                      {/* Under the line, as «Anular» sits under an abono: beside «Abonar» it squeezed the name to a word. */}
                      {anterior && puedeAnotarDeuda && (
                        <button
                          type="button"
                          className="btn-texto mt-0.5 gap-1 text-xs text-slate-500"
                          onClick={() => setDeuda(c)}
                        >
                          <Ico icono="editar" className="size-3.5" />
                          {t('comun.corregir')}
                        </button>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <span className="tabular">{libros.dinero(saldo)}</span>
                      {puedeRegistrar && (
                        <button
                          type="button"
                          className="btn-texto gap-1 text-xs text-slate-400"
                          onClick={() => setAbonando({ tipo: 'compra', id: c.id, saldo })}
                        >
                          <Ico icono="pagar" className="size-3.5" />
                          {t('comun.abonar')}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
              {gastosPorPagar.map((g) => {
                const saldo = pendienteDeGasto(g);
                return (
                  <li key={g.id} className="flex items-center justify-between gap-3 text-sm">
                    <div className="min-w-0">
                      <p className="truncate">{g.descripcion}</p>
                      <p className="text-xs text-slate-500">
                        {etiquetaCategoriaGasto(g.categoria)} · {g.fecha}
                        {(g.pagado ?? 0) > 0 ? t('gastos.abonado', { monto: libros.dinero(g.pagado!) }) : ''}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <span className="tabular">{libros.dinero(saldo)}</span>
                      {puedeRegistrar && (
                        <button
                          type="button"
                          className="btn-texto gap-1 text-xs text-slate-400"
                          onClick={() => setAbonando({ tipo: 'gasto', id: g.id, saldo })}
                        >
                          <Ico icono="pagar" className="size-3.5" />
                          {t('comun.abonar')}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            {puedeAnotarDeuda && (
              <button
                type="button"
                className="btn-texto mt-3 gap-1 text-xs text-slate-400"
                onClick={() => setDeuda('nueva')}
              >
                <Ico icono="nuevo" className="size-3.5" />
                {t('gastos.deudaAnterior')}
              </button>
            )}
          </section>
          <ListaGastosFijos
            fijos={config.gastosFijos}
            puedeRegistrar={puedeRegistrar}
            onAbrir={setEditandoFijo}
            onNuevo={() => setEditandoFijo('nuevo')}
            dinero={enFila}
          />
        </div>
      )}

      {vista === 'pagos' &&
        pagos &&
        (pagos.length > 0 ? (
          <section className="tarjeta" data-gastos-pagos>
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="text-sm font-semibold text-slate-400">{t('gastos.pagosAProveedores')}</h2>
              <VerTodos to="/gastos/pagos" />
            </div>
            <p className="mb-2 text-xs text-slate-500">{t('gastos.pagosAProveedoresAyuda')}</p>
            <ul className="flex flex-col gap-1 text-sm">
              {pagos.map((p) => {
                const nombre = (p.proveedorId ? porProveedor.get(p.proveedorId) : undefined) ?? t('gastos.nadieEnParticular');
                return (
                  <FilaPagoProveedor
                    key={p.id}
                    pago={p}
                    nombre={nombre}
                    {...(enFila ? { dinero: enFila } : {})}
                    {...(puedeRegistrar && p.pago && p.anulable ? { onAnular: () => setAnulandoPago({ deuda: p.deuda, pago: p.pago!, nombre }) } : {})}
                  />
                );
              })}
            </ul>
          </section>
        ) : (
          <Vacio mensaje={t('pagosProv.vacio')} />
        ))}

      {vista === 'dueno' && (
        <>
          {puedeRegistrar && (
            <button
              type="button"
              className="btn-secundario mb-4 w-full text-sm"
              onClick={() => setDuenoAbierto(true)}
            >
              <Ico icono="retirar" />
              {t('gastos.dineroDelDueno')}
            </button>
          )}

          {movimientos &&
            (movimientos.length > 0 ? (
              <section className="tarjeta" data-gastos-dueno>
                <div className="mb-2 flex items-baseline justify-between gap-3">
                  <h2 className="text-sm font-semibold text-slate-400">{t('gastos.dineroDelDueno')}</h2>
                  <VerTodos to="/gastos/dueno" />
                </div>
                <ul className="flex flex-col gap-1 text-sm">
                  {movimientos
                    .slice()
                    .sort((a, b) => b.fecha.localeCompare(a.fecha))
                    .slice(0, 20)
                    .map((mv) => (
                      <FilaMovimientoDueno
                        key={mv.id}
                        movimiento={mv}
                        {...(enFila ? { dinero: enFila } : {})}
                        {...(puedeRegistrar ? { onAnular: () => setAnulandoMovimiento(mv) } : {})}
                      />
                    ))}
                </ul>
              </section>
            ) : (
              <Vacio mensaje={t('duenoHist.vacio')} />
            ))}
        </>
      )}
      {nuevoAbierto && (
        <Modal
          // A month of a fixed expense is titled by its name: its words below say which month.
          titulo={deFijo ? deFijo.fijo.descripcion : t('gastos.nuevo')}
          // Closed, what was typed waits for the next «Nuevo gasto»: a sheet shut by a slip is not
          // a bill to type again. Saving clears it, keeping the category, the day and how it was paid.
          onCerrar={cerrarNuevo}
          pie={
            <button
              type="button"
              className="btn-primario w-full"
              data-guia="gasto-guardar"
              disabled={trabajando || !borrador.descripcion.trim() || !parseMoney(borrador.monto)}
              onClick={() => void guardarGasto()}
            >
              <Ico icono="guardar" />
              {t('gastos.guardar')}
            </button>
          }
        >
          <div className="flex flex-col gap-4" data-gasto-nuevo>
            <CamposGasto
              valor={borrador}
              cambiar={cambiarBorrador}
              proveedores={proveedores}
              tasa={config.fxTasaOficial}
              enfocar
              // With a till to pay from, the switch below says where the cash comes from instead;
              // with cash and none, why: no till open anywhere, or the expense of another day.
              ayudaPago={
                gaveta.aplica
                  ? undefined
                  : conEfectivo(borrador) && gaveta.sabido && gaveta.cajas.length === 0
                    ? t('gastos.ayudaPagoSinCaja')
                    : conEfectivo(borrador) && borrador.fecha !== hoy() && gaveta.cajas.length > 0
                      ? t('gastos.ayudaPagoOtroDia')
                      : t('gastos.ayudaPago')
              }
              trasFormaPago={<SaleDeLaGaveta gaveta={gaveta} />}
            />
            {repetido && (
              <p className="rounded-lg bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
                {t('gastos.parecido', {
                  monto: formatMoney(repetido.montoCordobas),
                  descripcion: repetido.descripcion,
                })}
              </p>
            )}
            {/* The rent, the internet, the light: kept as a fixed expense, asked for each time it falls due. */}
            {!deFijo && (
              <div className="flex flex-col gap-3 rounded-xl bg-slate-950/40 px-3 py-1 ring-1 ring-white/5 ring-inset" data-gasto-repite>
                <Interruptor
                  etiqueta={t('fijos.seRepite')}
                  descripcion={t('fijos.seRepiteAyuda', { dia: Number(borrador.fecha.slice(8, 10)) || 1 })}
                  checked={repite}
                  onChange={setRepite}
                />
                {repite && (
                  <>
                    <Selector etiqueta={t('fijos.cada')} value={String(repiteCada)} onChange={(e) => setRepiteCada(Number(e.target.value))}>
                      {CADA_MESES.map((n) => (
                        <option key={n} value={n}>
                          {t(CADA[n])}
                        </option>
                      ))}
                    </Selector>
                    <Interruptor
                      etiqueta={t('fijos.montoVaria')}
                      descripcion={t('fijos.montoVariaAyuda')}
                      checked={repiteVaria}
                      onChange={setRepiteVaria}
                    />
                  </>
                )}
              </div>
            )}
            {errorGasto && <p className="text-sm text-rose-400">{errorGasto}</p>}
          </div>
        </Modal>
      )}
      {editandoFijo && (
        <EditarGastoFijo
          // A new sheet for each: what was typed for one fixed expense is not another's.
          key={editandoFijo === 'nuevo' ? 'nuevo' : editandoFijo.id}
          fijo={editandoFijo === 'nuevo' ? null : editandoFijo}
          proveedores={proveedores}
          onGuardar={async (f) => {
            await guardarFijos(
              editandoFijo === 'nuevo' ? [...config.gastosFijos, f] : config.gastosFijos.map((x) => (x.id === f.id ? f : x)),
            );
            setEditandoFijo(null);
          }}
          onQuitar={async (f) => {
            await guardarFijos(config.gastosFijos.filter((x) => x.id !== f.id));
            setEditandoFijo(null);
          }}
          onCerrar={() => setEditandoFijo(null)}
        />
      )}
      {omitiendo && (
        <Confirmar
          titulo={t('fijos.omitirTitulo', { descripcion: omitiendo.fijo.descripcion, mes: nombreMes(omitiendo.periodo) })}
          mensaje={t('fijos.omitirMensaje')}
          confirmar={t('fijos.esteMesNo')}
          icono="saltar"
          onCerrar={() => setOmitiendo(null)}
          onConfirmar={() => {
            const o = omitiendo;
            setOmitiendo(null);
            void guardarFijos(
              config.gastosFijos.map((x) => (x.id === o.fijo.id ? { ...x, omitidos: [...x.omitidos, o.periodo].slice(-240) } : x)),
            );
          }}
        />
      )}
      {/* A sheet over the owner's tab, as «Nuevo gasto»'s is over any (the owner, 2026-10-06). */}
      {duenoAbierto && (
        <Modal
          titulo={t('gastos.dineroDelDueno')}
          onCerrar={() => setDuenoAbierto(false)}
          pie={
            <button
              type="button"
              className="btn-primario w-full"
              disabled={trabajando || !parseMoney(montoDueno)}
              onClick={() => void guardarDueno()}
            >
              <Ico icono="guardar" />
              {t('caja.registrar')}
            </button>
          }
        >
          <div className="flex flex-col gap-4" data-gastos-dueno-hoja>
            <Selector
              etiqueta={t('gastos.movimiento')}
              value={tipoDueno}
              onChange={(e) => setTipoDueno(e.target.value as 'retiro' | 'aporte')}
            >
              <option value="retiro">{t('gastos.duenoSaca')}</option>
              <option value="aporte">{t('gastos.duenoMete')}</option>
            </Selector>
            <Campo
              etiqueta={t(duenoEnDolares ? 'gastos.montoDolares' : 'gastos.montoCordobas')}
              inputMode="decimal"
              className="tabular text-lg"
              value={montoDueno}
              onChange={(e) => setMontoDueno(e.target.value)}
              placeholder="0.00"
              autoFocus
            />
            <Selector
              etiqueta={t(tipoDueno === 'retiro' ? 'gastos.deDondeSale' : 'gastos.aDondeEntra')}
              value={cuentaDueno}
              onChange={(e) => setCuentaDueno(e.target.value as Exclude<Forma, 'credito'>)}
            >
              {FORMAS.filter((f) => f.valor !== 'credito').map((f) => (
                <option key={f.valor} value={f.valor}>
                  {t(f.nombre)}
                </option>
              ))}
            </Selector>
            {cuentaDueno === 'transferencia' && (
              <ElegirCuenta
                moneda="USD"
                valor={bancoDueno}
                onCambiar={setBancoElegidoDueno}
                etiqueta={t(tipoDueno === 'retiro' ? 'bancos.saleDe' : 'bancos.entraA')}
              />
            )}
            <Campo
              maxLength={200}
              etiqueta={t('comun.motivo')}
              value={motivoDueno}
              onChange={(e) => setMotivoDueno(e.target.value)}
              autoComplete="off"
            />
            <p className="text-xs text-slate-500">
              {t('gastos.explicacionDueno')}
            </p>
          </div>
        </Modal>
      )}
      {anulandoMovimiento && <AnularMovimiento movimiento={anulandoMovimiento} onCerrar={() => setAnulandoMovimiento(null)} />}
      {anulandoPago && <AnularPago {...anulandoPago} onCerrar={() => setAnulandoPago(null)} />}
      {abonando && <AbonarDeuda deuda={abonando} onCerrar={() => setAbonando(null)} />}

      {deuda && (
        <DeudaAnterior {...(deuda === 'nueva' ? {} : { deuda })} onCerrar={() => setDeuda(null)} />
      )}

      {viendo && (
        <DetalleGasto
          // A new sheet for each expense: a reason half typed on one is not the other's.
          key={viendo}
          gastoId={viendo}
          puedeCorregir={puedeRegistrar}
          onVer={setViendo}
          onCorregir={(g) => {
            setViendo(null);
            setCorrigiendo(g);
          }}
          onCerrar={() => setViendo(null)}
        />
      )}

      {corrigiendo && (
        <CorregirGasto
          gasto={corrigiendo}
          proveedores={proveedores}
          tasaOficial={config.fxTasaOficial}
          onCerrar={() => {
            setViendo(corrigiendo.id);
            setCorrigiendo(null);
          }}
          onListo={(nuevoId) => {
            setCorrigiendo(null);
            setViendo(nuevoId);
          }}
        />
      )}
    </Pagina>
  );
}

/**
 * The fields of an expense, as the new one is typed and as a correction retypes one.
 *
 * The tutorial's anchors come along to the correction too, harmlessly: a lesson points at the
 * first match in the page, and the correction's sheet is never open while a lesson is walking
 * through the new expense's.
 */
function CamposGasto({
  valor,
  cambiar,
  proveedores,
  tasa,
  enfocar = false,
  ayudaPago,
  trasFormaPago,
}: {
  valor: Borrador;
  cambiar: (parte: Partial<Borrador>) => void;
  proveedores: readonly { id: string; nombre: string; activo: boolean }[];
  /** The rate dollars go in at, C$ per US$ × 10,000. */
  tasa: number;
  enfocar?: boolean;
  ayudaPago?: string;
  /** Under how it was paid: where the cash comes from. */
  trasFormaPago?: ReactNode;
}) {
  const t = useT();
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  const cuenta = cuentaDelBorrador(config, valor);
  /** The part just added, whose amount takes the cursor. */
  const [enfocarParte, setEnfocarParte] = useState<string | null>(null);
  const resto = restoDelBorrador(valor);
  function agregarParte() {
    const pagoId = newId();
    cambiar({ partes: [...valor.partes, { pagoId, formaPago: 'efectivo_cordobas', monto: '', cuenta: null }] });
    setEnfocarParte(pagoId);
  }
  return (
    <>
      <div data-guia="gasto-categoria">
        <Selector
          etiqueta={t('gastos.categoria')}
          value={valor.categoria}
          onChange={(e) => cambiar({ categoria: e.target.value as CategoriaGasto })}
        >
          {CATEGORIAS_GASTO.map((c) => (
            <option key={c} value={c}>
              {etiquetaCategoriaGasto(c)}
            </option>
          ))}
        </Selector>
      </div>
      <div data-guia="gasto-descripcion">
        <Campo
          maxLength={200}
          etiqueta={t('gastos.deQueFue')}
          value={valor.descripcion}
          onChange={(e) => cambiar({ descripcion: e.target.value })}
          autoFocus={enfocar}
          autoComplete="off"
          ayuda={t('gastos.ayudaDescripcion')}
        />
      </div>
      <div className="grid grid-cols-2 gap-4" data-guia="gasto-monto">
        <Campo
          etiqueta={t(valor.moneda === 'USD' ? 'gastos.montoDolares' : 'gastos.montoCordobas')}
          inputMode="decimal"
          className="tabular text-lg"
          value={valor.monto}
          onChange={(e) => cambiar({ monto: e.target.value })}
          placeholder="0.00"
        />
        <Selector
          etiqueta={t('gastos.moneda')}
          value={valor.moneda}
          onChange={(e) => cambiar({ moneda: e.target.value as 'NIO' | 'USD' })}
          ayuda={valor.moneda === 'USD' ? t('gastos.aTasa', { tasa: (tasa / 10_000).toFixed(4) }) : undefined}
        >
          <option value="NIO">{t('gastos.cordobas')}</option>
          <option value="USD">{t('gastos.dolares')}</option>
        </Selector>
      </div>
      {/*
        The parts paid some other way, above what pays the rest, read in the order they add up, as on
        a purchase (the owner, 2026-10-16: «We need the option to pay with mixed methods of payment»).
      */}
      <PartesDelPago
        partes={valor.partes}
        moneda={valor.moneda}
        cambiar={(pagoId, patch) => cambiar({ partes: valor.partes.map((x) => (x.pagoId === pagoId ? { ...x, ...patch } : x)) })}
        quitar={(pagoId) => cambiar({ partes: valor.partes.filter((x) => x.pagoId !== pagoId) })}
        agregar={agregarParte}
        enfocar={enfocarParte}
        datos="gasto"
      />
      {pagaElResto(valor) ? (
        <Selector
          etiqueta={t(valor.partes.length > 0 ? 'compra.elResto' : 'gastos.comoSePago')}
          value={valor.formaPago}
          onChange={(e) => cambiar({ formaPago: e.target.value as Forma })}
          ayuda={valor.partes.length > 0 ? t('compra.restoAyuda', { monto: enMoneda(resto as Centavos, valor.moneda) }) : ayudaPago}
        >
          {FORMAS.map((f) => (
            <option key={f.valor} value={f.valor}>
              {t(f.nombre)}
            </option>
          ))}
        </Selector>
      ) : (
        <p className={`text-sm ${resto < 0 ? 'text-rose-300' : 'text-slate-400'}`} data-gasto-resto>
          {resto < 0 ? t('gastos.partesDeMas', { monto: enMoneda(-resto as Centavos, valor.moneda) }) : t('gastos.nadaQueda')}
        </p>
      )}
      {cuenta && pagaElResto(valor) && (
        <ElegirCuenta moneda={valor.moneda} valor={cuenta} onCambiar={(c) => cambiar({ cuenta: c })} etiqueta={t('bancos.pagaDe')} />
      )}
      {valor.partes.length === 0 && (
        <button type="button" className="btn-texto -mt-2 gap-1.5 self-start text-sm" onClick={agregarParte} data-gasto-pagar-parte>
          <Ico icono="nuevo" className="size-4" />
          {t('compra.pagarParte')}
        </button>
      )}
      {trasFormaPago}
      <Campo
        etiqueta={t('comun.fecha')}
        type="date"
        max={hoy()}
        value={valor.fecha}
        onChange={(e) => cambiar({ fecha: e.target.value })}
      />
      <ElegirEscribiendo
        etiqueta={t('gastos.proveedorOpcional')}
        placeholder={t('gastos.nadieEnParticular')}
        // One since taken off the list stays choosable on the expense that already names it.
        opciones={proveedores.filter((p) => p.activo || p.id === valor.proveedorId)}
        value={valor.proveedorId}
        onChange={(proveedorId) => cambiar({ proveedorId })}
        ninguno={t('gastos.nadieEnParticular')}
        datos="proveedor"
      />
      <Campo
        maxLength={80}
        etiqueta={t('comun.referencia')}
        value={valor.referencia}
        onChange={(e) => cambiar({ referencia: e.target.value })}
        autoComplete="off"
      />
      <TomarFotoCedula
        huella={valor.foto}
        onCambio={(foto) => cambiar({ foto })}
        textos={{
          etiqueta: t('gastos.fotoRecibo'),
          ayuda: t('gastos.ayudaFotoRecibo'),
          tomar: t('gastos.tomarFotoRecibo'),
          cambiar: t('gastos.cambiarFotoRecibo'),
        }}
      />
    </>
  );
}

/**
 * Which expenses the list shows, each condition in sight as on the sales and the purchases
 * (`ui/FiltrosHistorial.tsx`, the owner, 2026-10-06): the days as «Contabilidad» names them or
 * picked on a calendar, who was paid found by typing, the category, how it was paid, the order, and
 * the voided ones too.
 */
function FiltrosGastos({
  filtro,
  cambiar,
  proveedores,
}: {
  filtro: FiltroGastos;
  cambiar: (parte: Partial<FiltroGastos>) => void;
  proveedores: readonly { id: string; nombre: string }[];
}) {
  const t = useT();
  return (
    <FiltrosEnLinea
      buscar={{ texto: filtro.texto, onTexto: (texto) => cambiar({ texto }), placeholder: t('gastos.buscar') }}
      hayPuestos={filtrosPuestos(filtro)}
      onQuitarTodos={() => cambiar({ ...filtroInicial(), texto: filtro.texto })}
    >
      <FiltroFechas periodo={filtro.periodo} desde={filtro.desde} hasta={filtro.hasta} onCambio={cambiar} />
      <BuscarEnLista
        clave="proveedorId"
        etiqueta={t('compra.proveedor')}
        placeholder={t('historial.buscarProveedor')}
        opciones={proveedores}
        value={filtro.proveedorId}
        onChange={(proveedorId) => cambiar({ proveedorId })}
      />
      <FilaDesplegables>
        <Desplegable
          clave="categoria"
          etiqueta={t('gastos.categoria')}
          value={filtro.categoria}
          onChange={(v) => cambiar({ categoria: v as CategoriaGasto | '' })}
          elegido={filtro.categoria ? etiquetaCategoriaGasto(filtro.categoria) : undefined}
        >
          <option value="">{t('gastos.todasCategorias')}</option>
          {CATEGORIAS_GASTO.map((c) => (
            <option key={c} value={c}>
              {etiquetaCategoriaGasto(c)}
            </option>
          ))}
        </Desplegable>
        <Desplegable
          clave="forma"
          etiqueta={t('historial.forma')}
          value={filtro.forma}
          onChange={(v) => cambiar({ forma: v as FormaPago | '' })}
          elegido={filtro.forma ? t(nombreForma(filtro.forma)) : undefined}
        >
          <option value="">{t('historial.todas')}</option>
          {FORMAS.map((f) => (
            <option key={f.valor} value={f.valor}>
              {t(f.nombre)}
            </option>
          ))}
        </Desplegable>
        <Desplegable
          clave="orden"
          etiqueta={t('gastos.orden')}
          value={filtro.orden}
          onChange={(v) => cambiar({ orden: v as 'fecha' | 'monto' })}
          elegido={filtro.orden === 'monto' ? t('gastos.ordenMonto') : undefined}
        >
          <option value="fecha">{t('gastos.ordenFecha')}</option>
          <option value="monto">{t('gastos.ordenMonto')}</option>
        </Desplegable>
      </FilaDesplegables>
      <Interruptor etiqueta={t('gastos.verAnulados')} checked={filtro.conAnulados} onChange={(v) => cambiar({ conAnulados: v })} />
    </FiltrosEnLinea>
  );
}

/** `dinero`: an amount kept on one line, the label wrapping instead («Efectivo C$ · de la gaveta» at 320 px). */
function Fila({ etiqueta, valor, dinero = false }: { etiqueta: string; valor: string; dinero?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className={`text-sm text-slate-400 ${dinero ? 'min-w-0' : 'shrink-0'}`}>{etiqueta}</span>
      <span className={dinero ? 'tabular shrink-0 text-right text-sm whitespace-nowrap' : 'min-w-0 break-words text-right text-sm'}>{valor}</span>
    </div>
  );
}

/**
 * Everything recorded about one expense: what, when, how it was paid, who recorded it, what has
 * been paid of it, the receipt; and, voided, who voided it, why, and what it became.
 *
 * Who and when come from the log each phone keeps, as a sale's sheet finds who rang it up: the
 * expense's row holds only what the books need. So does the link between a correction and what
 * it replaced, which the void carries (`reemplazo`).
 */
function DetalleGasto({
  gastoId,
  puedeCorregir,
  onVer,
  onCorregir,
  onCerrar,
}: {
  gastoId: string;
  puedeCorregir: boolean;
  onVer: (id: string) => void;
  onCorregir: (g: Gasto) => void;
  onCerrar: () => void;
}) {
  const t = useT();
  const pin = usePinParaAnular(false);
  const [anulando, setAnulando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [pidiendoPin, setPidiendoPin] = useState(false);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [anulandoPago, setAnulandoPago] = useState<PagoProveedor | null>(null);
  /**
   * Whether the books would take the void, asked as it opens: the money it paid with counted
   * since, they refuse it (`sinContar`), and the sheet says why before a reason and a PIN.
   */
  const noSeAnula = useLiveQuery(
    async () => (anulando ? probarEvento('gasto.anulado', { gastoId, motivo: '·', sinContar: true }) : null),
    [gastoId, anulando],
  );
  const datos = useLiveQuery(async () => {
    const g = await db.gastos.get(gastoId);
    if (!g) return null;
    const sinEfecto = await leerSinEfecto();
    const eventos = (await db.eventos.where('type').anyOf('gasto.registrado', 'faltante.explicado', 'gasto.anulado', 'gasto.pagado').toArray())
      .filter((e) => tuvoEfecto(e, sinEfecto))
      .sort((a, b) => a.clientTs.localeCompare(b.clientTs));
    const de = (e: { payload?: unknown }) => (e.payload as { gastoId?: string }).gastoId;
    /** The void of `x` that took, with who voided it and when. */
    const anulacionDe = (x: Gasto) =>
      anulacionQueQuedo(
        x,
        eventos
          .filter((e) => e.type === 'gasto.anulado' && de(e) === x.id)
          .map((e) => ({ ...(e.payload as { motivo: string; reemplazo?: string }), actorId: e.actorId, clientTs: e.clientTs })),
      );
    // A shortage explained was recorded as one (`faltante.explicado`).
    const registro = eventos.find((e) => (e.type === 'gasto.registrado' || e.type === 'faltante.explicado') && de(e) === gastoId);
    const anulacion = anulacionDe(g);
    const nuevo = anulacion?.reemplazo ? await db.gastos.get(anulacion.reemplazo) : undefined;
    const corrige = eventos.find((e) => e.type === 'gasto.anulado' && (e.payload as { reemplazo?: string }).reemplazo === gastoId);
    const anterior = corrige ? await db.gastos.get(de(corrige) ?? '') : undefined;
    const pagos = eventos
      .filter((e) => e.type === 'gasto.pagado' && de(e) === gastoId)
      .flatMap((e) => {
        const p = e.payload as { fecha: string; monto: number; formaPago: string; pagoId?: string };
        // As the expense keeps it, if it named itself: voidable, or voided. One it does not keep, the books refused.
        const pago = pagoDeLaDeuda(p.pagoId, g);
        return pago === null ? [] : [{ id: e.id, ...p, pago }];
      });
    const proveedor = g.proveedorId ? await db.proveedores.get(g.proveedorId) : undefined;
    const usuarios = new Map((await db.usuarios.toArray()).map((u) => [u.id, u.nombre]));
    return {
      g,
      registro,
      anulacion,
      nuevo,
      // Only if the old one really was voided in its favour.
      anterior: anterior && anulacionDe(anterior)?.reemplazo === gastoId ? anterior : undefined,
      pagos,
      proveedor,
      usuarios,
    };
  }, [gastoId]);

  if (!datos) return null;
  const { g, registro, anulacion, nuevo, anterior, pagos, proveedor, usuarios } = datos;
  const nombre = (id: string) => usuarios.get(id) ?? t('ventas.alguien');
  /** Paid in parts (`conElGasto`): those that stand, a part voided on its own being paid no more. */
  const enPartes = (g.pagos ?? []).some((x) => x.conElGasto);
  const partes = partesDelGasto(g);

  async function anular() {
    setTrabajando(true);
    try {
      const { rechazo } = await emitirYVer('gasto.anulado', { gastoId, motivo: motivo.trim(), sinContar: true });
      setError(rechazo ? t('gastos.noSeAnulo', { motivo: rechazo }) : null);
      if (!rechazo) setAnulando(false);
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <Modal titulo={g.descripcion} onCerrar={onCerrar}>
      {g.anulado && (
        <div className="mb-4 rounded-xl bg-rose-500/10 px-4 py-3 text-sm text-rose-200" data-gasto-anulado>
          <p>
            {t(nuevo ? 'gastos.corregidoPor' : 'gastos.anuladoPor', {
              nombre: anulacion ? nombre(anulacion.actorId) : t('ventas.alguien'),
              cuando: anulacion ? cuandoConHora(anulacion.clientTs) : '—',
            })}
          </p>
          {g.motivoAnulacion && <p className="mt-1">{t('gastos.motivo', { motivo: g.motivoAnulacion })}</p>}
          {nuevo && (
            <button type="button" className="btn-texto mt-2 gap-1 text-sm text-rose-100" onClick={() => onVer(nuevo.id)}>
              <Ico icono="adelante" className="size-4" />
              {t('gastos.verCorreccion')}
            </button>
          )}
        </div>
      )}
      {g.faltanteDe && (
        <div className="mb-4 rounded-xl bg-slate-800 px-4 py-3 text-sm text-slate-300" data-gasto-faltante>
          {t('gastos.explicaFaltante', { fecha: fechaCorta(g.fecha) })}
        </div>
      )}
      {anterior && (
        <div className="mb-4 rounded-xl bg-slate-800 px-4 py-3 text-sm text-slate-300" data-gasto-corrige>
          <p>{t('gastos.corrigeA')}</p>
          <button type="button" className="btn-texto mt-2 gap-1 text-sm" onClick={() => onVer(anterior.id)}>
            <Ico icono="atras" className="size-4" />
            {t('gastos.verAnterior')}
          </button>
        </div>
      )}

      <p className={`tabular text-2xl font-semibold ${g.anulado ? 'text-slate-500 line-through' : ''}`}>
        {formatMoney(g.montoCordobas)}
      </p>
      {g.moneda === 'USD' && g.tasaCambio !== null && (
        <p className="tabular mt-0.5 text-sm text-slate-400">
          {t('gastos.enDolares', {
            monto: (g.monto / 100).toFixed(2),
            tasa: (g.tasaCambio / 10_000).toFixed(4),
          })}
        </p>
      )}

      <div className="mt-4 flex flex-col gap-2" data-gasto-datos>
        <Fila etiqueta={t('gastos.categoria')} valor={etiquetaCategoriaGasto(g.categoria)} />
        <Fila etiqueta={t('comun.fecha')} valor={g.fecha} />
        <Fila
          etiqueta={t('gastos.formaPago')}
          valor={
            g.faltanteDe
              ? t('gastos.formaFaltante')
              : enPartes
                ? t('gastos.formaEnPartes')
                : `${t(nombreForma(g.formaPago))}${g.sesionId ? t('gastos.deLaGaveta') : ''}`
          }
        />
        {/* Each part and what they left, a line each, as a purchase's (2026-10-16); voided, nothing was paid. */}
        {enPartes && !g.anulado && (
          <div className="flex flex-col gap-1 border-l border-slate-800 pl-3" data-gasto-partes-pagadas>
            {partes.map((x) => (
              <Fila key={x.id} etiqueta={`${t(nombreForma(x.formaPago))}${x.sesionId ? t('gastos.deLaGaveta') : ''}`} valor={formatMoney(x.monto)} dinero />
            ))}
            <Fila
              etiqueta={`${t(nombreForma(g.formaPago))}${g.sesionId ? t('gastos.deLaGaveta') : ''}`}
              valor={formatMoney(partes.reduce((a, x) => a - x.monto, g.montoCordobas as number) as Centavos)}
              dinero
            />
          </div>
        )}
        {proveedor && <Fila etiqueta={t('gastos.proveedor')} valor={proveedor.nombre} />}
        {g.referencia && <Fila etiqueta={t('comun.referencia')} valor={g.referencia} />}
        {g.nota && <Fila etiqueta={t('comun.nota')} valor={g.nota} />}
        {registro && (
          <Fila etiqueta={t('gastos.registradoPor')} valor={`${nombre(registro.actorId)} · ${cuandoConHora(registro.clientTs)}`} />
        )}
      </div>

      {g.formaPago === 'credito' && (
        <div className="mt-4 flex flex-col gap-2 border-t border-slate-800 pt-3">
          <Fila etiqueta={t('gastos.pagado')} valor={formatMoney((g.pagado ?? 0) as Centavos)} />
          {!g.anulado && <Fila etiqueta={t('gastos.falta')} valor={formatMoney(pendienteDeGasto(g))} />}
          {pagos.length > 0 && (
            <>
              <p className="mt-2 text-xs font-semibold text-slate-400">{t('gastos.pagos')}</p>
              <ul className="flex flex-col gap-1">
                {pagos.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-3 text-sm">
                    <span className={p.pago?.anulado ? 'text-slate-500 line-through' : 'text-slate-400'}>
                      {p.fecha} · {t(nombreForma(p.formaPago))}
                    </span>
                    <span className="flex shrink-0 items-center gap-3">
                      <span className={`tabular ${p.pago?.anulado ? 'text-slate-500 line-through' : ''}`}>{formatMoney(p.monto as Centavos)}</span>
                      {p.pago?.anulado ? (
                        <span className="text-xs text-rose-400">{t('gastos.anulado')}</span>
                      ) : (
                        puedeCorregir &&
                        p.pago && (
                          <button
                            type="button"
                            className="btn-texto gap-1 text-xs text-slate-400"
                            onClick={() => setAnulandoPago(p.pago!)}
                            data-gasto-anular-pago
                          >
                            <Ico icono="anular" className="size-3.5" />
                            {t('comun.anular')}
                          </button>
                        )
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {g.foto && (
        <div className="mt-4">
          <VerFotoCedula huella={g.foto} titulo={t('gastos.fotoRecibo')} />
        </div>
      )}

      {puedeCorregir && !g.anulado && (
        <div className="mt-5 flex flex-col gap-3">
          {!sePuedeCorregir(g) ? (
            <p className="text-sm text-slate-400">{t('gastos.conPagos')}</p>
          ) : anulando ? (
            <div className="flex flex-col gap-3 rounded-xl bg-slate-950/40 p-4 ring-1 ring-white/5 ring-inset">
              {noSeAnula ? (
                <p className="rounded-xl bg-amber-500/10 px-4 py-3 text-sm text-amber-200" data-gasto-no-se-anula>
                  {t('anular.noSePuede', { motivo: noSeAnula })}
                </p>
              ) : (
                <Campo
                  etiqueta={t('gastos.porQueAnula')}
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  autoFocus
                  autoComplete="off"
                  maxLength={200}
                />
              )}
              {error && <p className="text-sm text-rose-400">{error}</p>}
              <div className="flex gap-3">
                <button
                  type="button"
                  className="btn-secundario flex-1"
                  onClick={() => {
                    setAnulando(false);
                    setError(null);
                  }}
                >
                  <Ico icono="cancelar" />
                  {t('comun.cancelar')}
                </button>
                {!noSeAnula && (
                  <button
                    type="button"
                    className="btn-primario flex-1"
                    disabled={noSeAnula !== null || !motivo.trim() || trabajando}
                    onClick={() => setPidiendoPin(true)}
                  >
                    <Ico icono="anular" />
                    {t('comun.anular')}
                  </button>
                )}
              </div>
            </div>
          ) : g.faltanteDe ? (
            // Corrected, it would be paid for again out of the cash: voided, it is a shortage again.
            <>
              <p className="text-sm text-slate-400">{t('gastos.explicacionNoSeCorrige')}</p>
              <button type="button" className="btn-secundario w-full" onClick={() => setAnulando(true)} data-gasto-anular-explicacion>
                <Ico icono="anular" />
                {t('gastos.anularExplicacion')}
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn-secundario w-full" onClick={() => onCorregir(g)}>
                <Ico icono="editar" />
                {t('comun.corregir')}
              </button>
              <button
                type="button"
                className="btn-texto gap-1.5 text-sm text-slate-500"
                onClick={() => setAnulando(true)}
              >
                <Ico icono="anular" className="size-4" />
                {t('preguntar.anularGasto')}
              </button>
            </>
          )}
        </div>
      )}

      {/* The owner's own word that it is the owner: a phone left signed in voids nothing by itself. */}
      {pidiendoPin && (
        <PedirPin
          titulo={pin.titulo}
          motivo={t('gastos.pinAnular', { descripcion: g.descripcion })}
          verificar={pin.verificar}
          donde={pin.donde}
          onCancelar={() => setPidiendoPin(false)}
          onAutorizado={() => {
            setPidiendoPin(false);
            void anular();
          }}
        />
      )}
      {anulandoPago && (
        <AnularPago
          deuda={{ tipo: 'gasto', id: g.id }}
          pago={anulandoPago}
          nombre={proveedor?.nombre ?? t('gastos.nadieEnParticular')}
          onCerrar={() => setAnulandoPago(null)}
        />
      )}
    </Modal>
  );
}

/**
 * An expense retyped as it should have been, in its place.
 *
 * The books change nothing in place: the new record goes in first, then the old one is voided
 * naming it, and the books refuse that void unless the new one stands (core `gastos.ts`). Both
 * take the owner, and the owner's PIN is asked for here, so a phone left signed in as the owner
 * changes nothing by itself.
 */
function CorregirGasto({
  gasto,
  proveedores,
  tasaOficial,
  onCerrar,
  onListo,
}: {
  gasto: Gasto;
  proveedores: readonly { id: string; nombre: string; activo: boolean }[];
  tasaOficial: number;
  onCerrar: () => void;
  onListo: (nuevoId: string) => void;
}) {
  const t = useT();
  const pin = usePinParaAnular(false);
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  const [original] = useState(() => borradorDe(gasto));
  const [valor, setValor] = useState<Borrador>(original);
  const cambiar = (parte: Partial<Borrador>) => setValor((b) => ({ ...b, ...parte }));
  const [motivo, setMotivo] = useState('');
  const [pidiendoPin, setPidiendoPin] = useState(false);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Voided first, and then the new record refused: there is nothing left here to correct. */
  const [sinCorregir, setSinCorregir] = useState(false);

  /** The till it came out of, the rest's or a part's: one till to an expense. */
  const suCaja = gasto.sesionId ?? partesDelGasto(gasto).find((x) => x.sesionId)?.sesionId ?? null;
  const caja = useLiveQuery(async () => (suCaja ? ((await db.sesiones.get(suCaja)) ?? null) : null), [suCaja]);
  const abierta = caja?.estado === 'abierta';
  const sesionId = cajaDeCorreccion(gasto, conEfectivo(valor) ? 'efectivo_cordobas' : valor.formaPago, () => abierta);
  // Dollars go in at the rate the expense was recorded at, unless it was not in dollars before.
  const tasa = gasto.moneda === 'USD' && gasto.tasaCambio !== null ? gasto.tasaCambio : tasaOficial;
  const monto = parseMoney(valor.monto);
  // Against the drawer, what the new record takes out of it counts in córdobas, as the books count it;
  // what the old one gives back, in córdobas as they keep it.
  const enCordobas = monto === null ? 0 : (aCordobas(valor, enEfectivo(valor), tasa) ?? 0);
  const vuelve = deLaGavetaDelGasto(gasto);
  const enGaveta = sesionId && caja ? efectivoEsperado(caja) : null;
  const orden = ordenDeCorreccion(enCordobas, vuelve, enGaveta);
  const noAlcanza =
    orden === 'noAlcanza' && enGaveta !== null
      ? t('gastos.correccionNoAlcanza', { monto: formatMoney((enGaveta + vuelve) as Centavos) })
      : null;
  /** The parts read and together under the whole, as a new expense's: what they leave is paid some way. */
  const partesBien = montosDePartes(valor).every((x) => x !== null && x > 0) && restoDelBorrador(valor) >= 0;
  const cambio = comoQueda(valor) !== comoQueda(original);
  /**
   * Counted since (the books would refuse to void it, `sinContar`): the count found the money as
   * it was, so what it was for can be put right, not the amount, the way it was paid or the day.
   */
  const contada = useLiveQuery(
    () => probarEvento('gasto.anulado', { gastoId: gasto.id, motivo: '·', sinContar: true }),
    [gasto.id],
  );
  /** Its parts as the money they move: each its way, its amount, and the account a transfer came from. */
  const partesComo = (b: Borrador) =>
    b.partes.map((x) => ({ formaPago: x.formaPago, monto: parseMoney(x.monto), cuenta: x.formaPago === 'transferencia' ? cuentaEnCordobas(config, x.cuenta) : null }));
  const mueveDinero =
    Boolean(contada) &&
    correccionMueveDinero(
      { ...original, monto: parseMoney(original.monto), cuenta: cuentaDelBorrador(config, original), partes: partesComo(original) },
      { ...valor, monto, cuenta: cuentaDelBorrador(config, valor), partes: partesComo(valor) },
    );
  const listo =
    cambio &&
    monto !== null &&
    monto > 0 &&
    partesBien &&
    valor.descripcion.trim() !== '' &&
    motivo.trim() !== '' &&
    !noAlcanza &&
    !sinCorregir &&
    contada !== undefined &&
    !mueveDinero;

  async function guardar() {
    if (!listo || monto === null) return;
    setPidiendoPin(false);
    setTrabajando(true);
    setError(null);
    try {
      const nuevoId = newId();
      const registro = {
        gastoId: nuevoId,
        fecha: valor.fecha,
        categoria: valor.categoria,
        descripcion: valor.descripcion.trim(),
        moneda: valor.moneda,
        monto,
        ...(valor.moneda === 'USD' ? { tasaCambio: tasa } : {}),
        ...pagoDelBorrador(config, valor, sesionId, true),
        proveedorId: valor.proveedorId || null,
        ...(valor.referencia.trim() ? { referencia: valor.referencia.trim() } : {}),
        ...(gasto.nota ? { nota: gasto.nota } : {}),
        ...(valor.foto ? { foto: valor.foto } : {}),
      };
      if (orden === 'anularPrimero') {
        // The old amount back in the drawer first, or the drawer cannot pay the new one.
        const viejo = await emitirYVer('gasto.anulado', {
          gastoId: gasto.id,
          motivo: motivo.trim(),
          // What replaces it, recorded next: a fixed expense's month is paid by it, not due again.
          reemplazo: nuevoId,
          reemplazoDespues: true,
          sinContar: true,
        });
        if (viejo.rechazo) {
          setError(t('gastos.noSeCorrigio', { motivo: viejo.rechazo }));
          return;
        }
        const nuevo = await emitirYVer('gasto.registrado', registro);
        if (nuevo.rechazo) {
          setSinCorregir(true);
          setError(t('gastos.anuladoSinCorreccion', { motivo: nuevo.rechazo }));
          return;
        }
      } else {
        const nuevo = await emitirYVer('gasto.registrado', registro);
        if (nuevo.rechazo) {
          setError(t('gastos.noSeCorrigio', { motivo: nuevo.rechazo }));
          return;
        }
        const viejo = await emitirYVer('gasto.anulado', { gastoId: gasto.id, motivo: motivo.trim(), reemplazo: nuevoId });
        if (viejo.rechazo) {
          setError(t('gastos.noSeCorrigio', { motivo: viejo.rechazo }));
          return;
        }
      }
      onListo(nuevoId);
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <Modal
      titulo={t('gastos.corregirTitulo')}
      onCerrar={onCerrar}
      pie={
        <button
          type="button"
          className="btn-primario w-full"
          disabled={!listo || trabajando}
          onClick={() => setPidiendoPin(true)}
        >
          <Ico icono="guardar" />
          {t('gastos.guardarCorreccion')}
        </button>
      }
    >
      <p className="mb-4 text-sm text-slate-400">{t('gastos.corregirExplicacion')}</p>
      {contada && (
        <p className="mb-4 rounded-xl bg-amber-500/10 px-4 py-3 text-sm text-amber-200" data-gasto-correccion-contada>
          {t('gastos.correccionContada', { motivo: contada })}
        </p>
      )}
      <div className="flex flex-col gap-4" data-gasto-correccion>
        <CamposGasto
          valor={valor}
          cambiar={cambiar}
          proveedores={proveedores}
          tasa={tasa}
          trasFormaPago={
            suCaja && conEfectivo(valor) && caja !== undefined ? (
              <p className="text-xs text-slate-500">
                {t(abierta ? 'gastos.correccionDeGaveta' : 'gastos.correccionDeCajaContada')}
              </p>
            ) : undefined
          }
        />
        <Campo
          etiqueta={t('gastos.queEstabaMal')}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          autoComplete="off"
          maxLength={200}
        />
        {(error ?? noAlcanza) && <p className="text-sm text-rose-400">{error ?? noAlcanza}</p>}
        {mueveDinero && !error && (
          <p className="text-sm text-rose-400" data-gasto-correccion-mueve>
            {t('gastos.correccionMueveDinero')}
          </p>
        )}
      </div>
      {pidiendoPin && (
        <PedirPin
          titulo={pin.titulo}
          motivo={t('gastos.pinCorregir', { descripcion: gasto.descripcion })}
          verificar={pin.verificar}
          donde={pin.donde}
          onAutorizado={() => void guardar()}
          onCancelar={() => setPidiendoPin(false)}
        />
      )}
    </Modal>
  );
}
